// Copyright (C) 2026, Lifefinder Systems International AB, all rights reserved.
import React, { useState } from 'react';

import { useAuth } from '../../../context/AuthContext';
import { useUsers } from '../../../context/UserContext';
import type { User } from '../../../types';
import {
  adminNotificationPreferencesAPI,
  NotificationPreferences,
  userAPI,
} from '../../../utils/apiClient';
import logger from '../../../utils/logger';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * Individual right flags. Rights are stored as a character string in the database.
 * S=SuperAdmin, A=Admin, C=Checker, F=Filter.
 *
 * Privilege model:
 *   Roles (hierarchy): SuperAdmin > Admin > User
 *   Capabilities (tillval, SuperAdmin-only): Checker (C), Filter (F)
 *
 * All four flags require SuperAdmin to grant — Admins can only manage users
 * who hold neither S nor A, and cannot change any rights field.
 */
const RIGHTS_FLAGS = [
  { char: 'S', label: 'SuperAdmin', superAdminOnly: true },
  { char: 'A', label: 'Admin', superAdminOnly: true },
  { char: 'C', label: 'CheckIn', superAdminOnly: true },
  { char: 'F', label: 'Filter', superAdminOnly: true },
] as const;

/** Toggle a single char in a rights string. */
const toggleRightsChar = (rights: string, char: string): string => {
  if (rights.includes(char)) {
    return rights.replace(char, '');
  }
  // Keep canonical order: SACF
  const order = ['S', 'A', 'C', 'F'];
  const combined = rights + char;
  return order.filter((c) => combined.includes(c)).join('');
};

/** Local edit state for a single user row */
interface UserEdit {
  username: string;
  rights: string;
  password: string;
}

/**
 * UserManagement Component - User CRUD for the Admin tab
 *
 * Displays all users in a table with inline editing and deletion.
 * New users can be added via the form at the bottom.
 * Accessible to admin users and above.
 */
export const UserManagement: React.FC = () => {
  const { isSuperAdmin } = useAuth();
  const { users, loading, error, fetchUsers, onDeleteUser, onAddUser } = useUsers();

  // SuperAdmin sees all flags; Admin sees none (rights are SuperAdmin-only)
  const visibleFlags = RIGHTS_FLAGS.filter((f) => !f.superAdminOnly || isSuperAdmin);

  // Admin only sees users with no S or A in their rights (strictly lower privilege)
  const visibleUsers = isSuperAdmin
    ? users
    : users.filter((u) => !u.rights?.includes('S') && !u.rights?.includes('A'));

  const [editingId, setEditingId] = useState<string | null>(null);
  const [editState, setEditState] = useState<UserEdit>({ username: '', rights: '', password: '' });
  const [addState, setAddState] = useState<UserEdit>({
    username: '',
    rights: '',
    password: '',
  });
  const [showAddForm, setShowAddForm] = useState<boolean>(false);
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
  const [saving, setSaving] = useState<boolean>(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  // Notification preferences for the currently edited user.
  const [notifPrefs, setNotifPrefs] = useState<NotificationPreferences | null>(null);
  const [notifPrefsLoading, setNotifPrefsLoading] = useState<boolean>(false);
  const [notifEmailInput, setNotifEmailInput] = useState<string>('');
  const [notifEmailError, setNotifEmailError] = useState<string | null>(null);
  const [notifSaving, setNotifSaving] = useState<boolean>(false);

  /** Begin editing a user row; loads notification preferences in the background. */
  const startEdit = (user: User): void => {
    setEditingId(user.id);
    setEditState({ username: user.username, rights: user.rights ?? '', password: '' });
    setConfirmDeleteId(null);
    setNotifPrefs(null);
    setNotifEmailInput('');
    setNotifEmailError(null);
    setNotifPrefsLoading(true);
    adminNotificationPreferencesAPI
      .get(user.id)
      .then(({ preferences }) => {
        setNotifPrefs(preferences);
        setNotifEmailInput(preferences.email ?? '');
      })
      .catch((err) => logger.error('UserManagement: Failed to load notification prefs', err))
      .finally(() => setNotifPrefsLoading(false));
  };

  /** Commit edits for the currently edited user by calling the API directly.
   * Using the UserContext two-step pattern (onEditUserX + onSubmitUserDataEdits) would
   * fail here because React state updates are async — userDataEdits would still be empty
   * when onSubmitUserDataEdits reads it.
   */
  const saveEdit = async (): Promise<void> => {
    if (!editingId) return;

    // Validate email before saving.
    const trimmedEmail = notifEmailInput.trim();
    if (trimmedEmail !== '' && !EMAIL_RE.test(trimmedEmail)) {
      setNotifEmailError('Enter a valid email address');
      return;
    }
    setNotifEmailError(null);

    setSaving(true);
    setSaveError(null);
    try {
      const user = users.find((u) => u.id === editingId);
      if (!user) return;

      const updates: { username?: string; rights?: string; password?: string } = {};
      if (editState.username !== user.username) updates.username = editState.username;
      // Rights changes are SuperAdmin-only — never include rights in payload for non-SuperAdmin
      if (isSuperAdmin && editState.rights !== (user.rights ?? ''))
        updates.rights = editState.rights;
      if (editState.password) updates.password = editState.password;

      if (Object.keys(updates).length > 0) {
        await userAPI.updateUser(user.id, updates);
        await fetchUsers();
      }

      // Save email address if it changed.
      if (notifPrefs !== null) {
        const newEmail = trimmedEmail === '' ? null : trimmedEmail;
        if (newEmail !== notifPrefs.email) {
          const { preferences } = await adminNotificationPreferencesAPI.update(editingId, {
            email: newEmail,
          });
          setNotifPrefs(preferences);
          setNotifEmailInput(preferences.email ?? '');
        }
      }

      setEditingId(null);
    } catch (err) {
      logger.error('Failed to save user edits:', err);
      setSaveError(err instanceof Error ? err.message : 'Failed to save changes');
    } finally {
      setSaving(false);
    }
  };

  /** Toggle notifyEmail or notifyPush immediately (no Save required). */
  const saveNotifToggle = async (
    userId: string,
    field: 'notifyEmail' | 'notifyPush',
    value: boolean
  ): Promise<void> => {
    if (!notifPrefs) return;
    setNotifSaving(true);
    try {
      const { preferences } = await adminNotificationPreferencesAPI.update(userId, {
        [field]: value,
      });
      setNotifPrefs(preferences);
    } catch (err) {
      logger.error('UserManagement: Failed to save notification toggle', err);
    } finally {
      setNotifSaving(false);
    }
  };

  /** Cancel in-progress edits */
  const cancelEdit = (): void => {
    setEditingId(null);
    setNotifPrefs(null);
    setNotifEmailError(null);
  };

  /** Delete a user after confirmation */
  const handleDelete = async (userId: string): Promise<void> => {
    setSaving(true);
    try {
      await onDeleteUser(userId);
      setConfirmDeleteId(null);
    } catch (err) {
      logger.error('Failed to delete user:', err);
    } finally {
      setSaving(false);
    }
  };

  /** Add a new user */
  const handleAdd = async (): Promise<void> => {
    if (!addState.username.trim()) return;
    setSaving(true);
    try {
      await onAddUser({
        name: addState.username,
        rights: addState.rights,
        password: addState.password,
      });
      setAddState({ username: '', rights: '', password: '' });
      setShowAddForm(false);
    } catch (err) {
      logger.error('Failed to add user:', err);
    } finally {
      setSaving(false);
    }
  };

  const cellStyle: React.CSSProperties = {
    padding: '0.8vmin 1.2vmin',
    borderBottom: '1px solid #f0f0f0',
    fontSize: 'max(12px, 1.5vmin)',
    verticalAlign: 'middle',
    textAlign: 'left',
  };

  const inputStyle: React.CSSProperties = {
    border: '1px solid #ccc',
    borderRadius: '0.4vmin',
    padding: '0.4vmin 0.7vmin',
    fontSize: 'max(12px, 1.5vmin)',
    width: '100%',
  };

  const actionBtn = (color: string): React.CSSProperties => ({
    background: color,
    color: '#fff',
    border: 'none',
    borderRadius: '0.4vmin',
    padding: '0.4vmin 1vmin',
    cursor: 'pointer',
    fontSize: 'max(11px, 1.4vmin)',
    marginLeft: '0.4vmin',
  });

  if (loading) {
    return (
      <div style={{ padding: '3vmin', textAlign: 'center', color: '#888' }}>Loading users…</div>
    );
  }

  return (
    <div style={{ padding: '1vmin 2vmin', overflow: 'auto', flex: 1 }}>
      {/* Header row */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '1.5vmin',
        }}
      >
        <h3 style={{ margin: 0, fontSize: 'max(14px, 1.8vmin)' }}>👥 Users</h3>
        <div style={{ display: 'flex', gap: '1vmin' }}>
          <button
            onClick={() => fetchUsers()}
            style={{ ...actionBtn('#607d8b'), padding: '0.5vmin 1.2vmin' }}
            title="Refresh users"
          >
            🔄 Refresh
          </button>
          <button
            onClick={() => {
              setShowAddForm((v) => !v);
              setConfirmDeleteId(null);
              setEditingId(null);
            }}
            style={{ ...actionBtn('#4caf50'), padding: '0.5vmin 1.2vmin' }}
          >
            {showAddForm ? '✕ Cancel' : '＋ Add user'}
          </button>
        </div>
      </div>

      {/* Error banner */}
      {(error || saveError) && (
        <div
          style={{
            background: '#ffebee',
            border: '1px solid #ef9a9a',
            borderRadius: '0.5vmin',
            padding: '0.8vmin 1.2vmin',
            marginBottom: '1.5vmin',
            color: '#c62828',
            fontSize: 'max(12px, 1.5vmin)',
          }}
        >
          {error || saveError}
        </div>
      )}

      {/* User table */}
      <table style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed' }}>
        <colgroup>
          <col style={{ width: '28%' }} />
          <col style={{ width: '18%' }} />
          <col style={{ width: '26%' }} />
          <col style={{ width: '28%' }} />
        </colgroup>
        <thead>
          <tr style={{ background: '#f5f5f5' }}>
            {['Username', 'Rights', 'New password', 'Actions'].map((h, i) => (
              <th
                key={h}
                style={{
                  ...cellStyle,
                  fontWeight: 600,
                  color: '#444',
                  borderBottom: '2px solid #e0e0e0',
                  textAlign: i === 3 ? 'right' : 'left',
                }}
              >
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {visibleUsers.length === 0 ? (
            <tr>
              <td
                colSpan={4}
                style={{ ...cellStyle, color: '#aaa', textAlign: 'center', padding: '3vmin' }}
              >
                No users found.
              </td>
            </tr>
          ) : (
            visibleUsers.map((user) => {
              const isEditing = editingId === user.id;
              const isConfirmingDelete = confirmDeleteId === user.id;
              return (
                <React.Fragment key={user.id}>
                  <tr style={{ background: isEditing ? '#f3e5f5' : undefined }}>
                    {/* Username */}
                    <td style={cellStyle}>
                      {isEditing ? (
                        <input
                          style={inputStyle}
                          value={editState.username}
                          onChange={(e) =>
                            setEditState((s) => ({ ...s, username: e.target.value }))
                          }
                        />
                      ) : (
                        user.username
                      )}
                    </td>

                    {/* Rights */}
                    <td style={cellStyle}>
                      {isEditing ? (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.3vmin' }}>
                          {visibleFlags.map((flag) => (
                            <label
                              key={flag.char}
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '0.5vmin',
                                cursor: 'pointer',
                                fontSize: 'max(11px, 1.4vmin)',
                              }}
                            >
                              <input
                                type="checkbox"
                                checked={editState.rights.includes(flag.char)}
                                onChange={() =>
                                  setEditState((s) => ({
                                    ...s,
                                    rights: toggleRightsChar(s.rights, flag.char),
                                  }))
                                }
                              />
                              {flag.label}
                            </label>
                          ))}
                        </div>
                      ) : (
                        <span
                          style={{
                            background: '#ede7f6',
                            color: '#7b2ff7',
                            borderRadius: '1vmin',
                            padding: '0.2vmin 0.8vmin',
                            fontSize: 'max(11px, 1.3vmin)',
                            fontWeight: 500,
                          }}
                        >
                          {user.rights ?? '—'}
                        </span>
                      )}
                    </td>

                    {/* Password (set only) */}
                    <td style={cellStyle}>
                      {isEditing ? (
                        <input
                          type="password"
                          placeholder="Leave blank to keep"
                          style={inputStyle}
                          value={editState.password}
                          onChange={(e) =>
                            setEditState((s) => ({ ...s, password: e.target.value }))
                          }
                        />
                      ) : (
                        <span style={{ color: '#bbb' }}>••••••••</span>
                      )}
                    </td>

                    {/* Actions */}
                    <td style={{ ...cellStyle, whiteSpace: 'nowrap', textAlign: 'right' }}>
                      {isEditing ? (
                        <>
                          <button onClick={saveEdit} disabled={saving} style={actionBtn('#7b2ff7')}>
                            Save
                          </button>
                          <button onClick={cancelEdit} style={actionBtn('#9e9e9e')}>
                            Cancel
                          </button>
                        </>
                      ) : isConfirmingDelete ? (
                        <>
                          <span
                            style={{
                              fontSize: 'max(11px, 1.4vmin)',
                              color: '#c62828',
                              marginRight: '0.5vmin',
                            }}
                          >
                            Delete?
                          </span>
                          <button
                            onClick={() => handleDelete(user.id)}
                            disabled={saving}
                            style={actionBtn('#e53935')}
                          >
                            Yes
                          </button>
                          <button
                            onClick={() => setConfirmDeleteId(null)}
                            style={actionBtn('#9e9e9e')}
                          >
                            No
                          </button>
                        </>
                      ) : (
                        <>
                          <button onClick={() => startEdit(user)} style={actionBtn('#1976d2')}>
                            Edit
                          </button>
                          <button
                            onClick={() => {
                              setConfirmDeleteId(user.id);
                              setEditingId(null);
                            }}
                            style={actionBtn('#e53935')}
                          >
                            Delete
                          </button>
                        </>
                      )}
                    </td>
                  </tr>

                  {/* Notification preferences — expanded section shown while editing */}
                  {isEditing && (
                    <tr key={`${user.id}-notif`} style={{ background: '#f8f9fa' }}>
                      <td
                        colSpan={4}
                        style={{
                          ...cellStyle,
                          borderTop: '1px solid #e0e0e0',
                          borderBottom: '2px solid #e0e0e0',
                          paddingTop: '0.8vmin',
                          paddingBottom: '1vmin',
                        }}
                      >
                        <div
                          style={{
                            fontWeight: 600,
                            fontSize: 'max(12px, 1.5vmin)',
                            marginBottom: '0.6vmin',
                            color: '#444',
                          }}
                        >
                          Alarm notifications
                        </div>

                        {notifPrefsLoading && (
                          <span style={{ color: '#888', fontSize: 'max(12px, 1.4vmin)' }}>
                            Loading…
                          </span>
                        )}

                        {!notifPrefsLoading && notifPrefs && (
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.6vmin' }}>
                            {/* Toggles row */}
                            <div
                              style={{
                                display: 'flex',
                                gap: '2vmin',
                                flexWrap: 'wrap',
                                alignItems: 'center',
                              }}
                            >
                              <label
                                style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '0.5vmin',
                                  fontSize: 'max(12px, 1.4vmin)',
                                  cursor: notifSaving ? 'default' : 'pointer',
                                }}
                              >
                                <input
                                  type="checkbox"
                                  checked={notifPrefs.notifyEmail}
                                  disabled={notifSaving}
                                  onChange={() =>
                                    saveNotifToggle(user.id, 'notifyEmail', !notifPrefs.notifyEmail)
                                  }
                                  style={{
                                    width: 'max(14px, 1.5vmin)',
                                    height: 'max(14px, 1.5vmin)',
                                    cursor: 'pointer',
                                  }}
                                />
                                Email notifications
                              </label>

                              <label
                                style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '0.5vmin',
                                  fontSize: 'max(12px, 1.4vmin)',
                                  cursor: notifSaving ? 'default' : 'pointer',
                                }}
                              >
                                <input
                                  type="checkbox"
                                  checked={notifPrefs.notifyPush}
                                  disabled={notifSaving}
                                  onChange={() =>
                                    saveNotifToggle(user.id, 'notifyPush', !notifPrefs.notifyPush)
                                  }
                                  style={{
                                    width: 'max(14px, 1.5vmin)',
                                    height: 'max(14px, 1.5vmin)',
                                    cursor: 'pointer',
                                  }}
                                />
                                Push notifications
                              </label>
                            </div>

                            {/* Email address */}
                            <div
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '0.8vmin',
                                flexWrap: 'wrap',
                              }}
                            >
                              <label
                                style={{
                                  fontSize: 'max(12px, 1.4vmin)',
                                  color: '#555',
                                  whiteSpace: 'nowrap',
                                }}
                              >
                                Notification email:
                              </label>
                              <input
                                type="email"
                                placeholder="user@example.com"
                                value={notifEmailInput}
                                onChange={(e) => {
                                  setNotifEmailInput(e.target.value);
                                  setNotifEmailError(null);
                                }}
                                style={{
                                  ...inputStyle,
                                  width: '16em',
                                  border: notifEmailError ? '1px solid #dc3545' : '1px solid #ccc',
                                }}
                              />
                              {notifEmailError && (
                                <span style={{ color: '#dc3545', fontSize: 'max(11px, 1.3vmin)' }}>
                                  {notifEmailError}
                                </span>
                              )}
                              <span style={{ color: '#888', fontSize: 'max(11px, 1.3vmin)' }}>
                                (saved with Save)
                              </span>
                            </div>

                            {/* Push info note */}
                            <div style={{ color: '#888', fontSize: 'max(11px, 1.3vmin)' }}>
                              Push delivery also requires the user to subscribe their browser via
                              the notification button in the app.
                            </div>
                          </div>
                        )}
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })
          )}
        </tbody>
      </table>

      {/* Add user form */}
      {showAddForm && (
        <div
          style={{
            marginTop: '2vmin',
            padding: '1.2vmin 2vmin 1.5vmin',
            background: '#f9fbe7',
            border: '1px solid #dce775',
            borderRadius: '0.8vmin',
          }}
        >
          <div
            style={{
              fontWeight: 700,
              marginBottom: '1vmin',
              fontSize: 'max(14px, 1.8vmin)',
              color: '#333',
            }}
          >
            New user
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1.8vmin' }}>
            {/* Username and password with clear separation */}
            <div style={{ display: 'flex', gap: '3vmin', flexWrap: 'wrap' }}>
              <div
                style={{ flex: '1 1 18vmin', display: 'flex', flexDirection: 'column', gap: '3px' }}
              >
                <label
                  style={{
                    fontSize: 'max(12px, 1.4vmin)',
                    color: '#444',
                    fontWeight: 600,
                  }}
                >
                  Username *
                </label>
                <input
                  style={inputStyle}
                  placeholder="Username"
                  value={addState.username}
                  onChange={(e) => setAddState((s) => ({ ...s, username: e.target.value }))}
                />
              </div>
              <div
                style={{ flex: '1 1 18vmin', display: 'flex', flexDirection: 'column', gap: '3px' }}
              >
                <label
                  style={{
                    fontSize: 'max(12px, 1.4vmin)',
                    color: '#444',
                    fontWeight: 600,
                  }}
                >
                  Password
                </label>
                <input
                  type="password"
                  style={inputStyle}
                  placeholder="Password"
                  value={addState.password}
                  onChange={(e) => setAddState((s) => ({ ...s, password: e.target.value }))}
                />
              </div>
            </div>

            {/* Rights in a clearly boxed section */}
            <div
              style={{
                background: '#fff',
                border: '1px solid #c5e1a5',
                borderRadius: '0.6vmin',
                padding: '1vmin 1.5vmin',
              }}
            >
              <div
                style={{
                  fontSize: 'max(12px, 1.4vmin)',
                  fontWeight: 600,
                  color: '#444',
                  marginBottom: '0.8vmin',
                  textAlign: 'center',
                }}
              >
                Rights
              </div>
              <div
                style={{
                  display: 'flex',
                  gap: '2.5vmin',
                  flexWrap: 'wrap',
                  justifyContent: 'center',
                }}
              >
                {visibleFlags.map((flag) => (
                  <label
                    key={flag.char}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.6vmin',
                      cursor: 'pointer',
                      fontSize: 'max(13px, 1.5vmin)',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={addState.rights.includes(flag.char)}
                      onChange={() =>
                        setAddState((s) => ({
                          ...s,
                          rights: toggleRightsChar(s.rights, flag.char),
                        }))
                      }
                      style={{
                        width: 'max(14px, 1.6vmin)',
                        height: 'max(14px, 1.6vmin)',
                        cursor: 'pointer',
                      }}
                    />
                    {flag.label}
                  </label>
                ))}
              </div>
            </div>

            {/* Add button on its own row, right-aligned */}
            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <button
                onClick={handleAdd}
                disabled={saving || !addState.username.trim()}
                style={{
                  ...actionBtn('#4caf50'),
                  padding: '0.8vmin 2.5vmin',
                  fontSize: 'max(13px, 1.5vmin)',
                  opacity: !addState.username.trim() ? 0.5 : 1,
                }}
              >
                Add user
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
